import { useCallback, useRef } from "react"
import { useInfiniteScrollSentinel } from "../../hooks/use-infinite-scroll-sentinel"
import { useProjects } from "../../hooks/use-projects"
import { usePropertyJournal } from "../../hooks/use-property-journal"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { JournalEmptyState } from "./journal-empty-state"
import { JournalError } from "./journal-error"
import { JournalRow } from "./journal-row"
import { JournalSkeleton } from "./journal-skeleton"

export function JournalList({
	propertyId,
	projectId,
}: {
	propertyId: string
	projectId?: string
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const query = usePropertyJournal(propertyId, projectId)
	const sentinelRef = useRef<HTMLDivElement>(null)

	// Stable marker mode (gate #2 fix A): markers appear iff the viewer can see
	// ≥1 project on this property — consistent from first paint, never
	// "source by absence", no relabeling when later pages load project events.
	// While projects are loading, mixed is false; markers pop in once when data
	// arrives. Acceptable trade-off — one-time pop vs mid-scroll churn.
	const projectsQuery = useProjects(propertyId)
	const mixed = !projectId && (projectsQuery.data?.length ?? 0) > 0

	// Stable callback — avoids observer churn on each render (per Task 6 self-review note).
	const onIntersect = useCallback(() => {
		query.fetchNextPage()
	}, [query.fetchNextPage])

	useInfiniteScrollSentinel({
		sentinelRef,
		enabled: !!query.hasNextPage && !query.isFetchingNextPage,
		onIntersect,
	})

	if (query.isLoading) return <JournalSkeleton />
	if (query.error) return <JournalError onRetry={() => query.refetch()} />
	const events = query.data?.pages.flatMap((p) => p.items) ?? []
	if (events.length === 0) return <JournalEmptyState />

	return (
		<div
			role="feed"
			aria-busy={query.isFetchingNextPage}
			aria-label={t.properties.tabs.journal}
		>
			{events.map((e) => (
				<JournalRow
					key={e.id}
					event={e}
					sourceLabel={
						mixed ? (e.project?.name ?? t.journal.sourceProperty) : undefined
					}
				/>
			))}
			{query.hasNextPage ? (
				<div ref={sentinelRef} aria-hidden="true" className="h-4" />
			) : null}
			{query.isFetchingNextPage ? <JournalSkeleton variant="compact" /> : null}
		</div>
	)
}
